'use client';

import { useRouter } from 'next/navigation';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

interface Account {
  id: string;
  name: string;
  slug: string;
}

interface AdminAccountSelectorProps {
  currentAccount: Account | null;
  accounts: Account[];
  onSwitch: (accountId: string) => Promise<{ error?: string }>;
}

/**
 * AdminAccountSelector - Dropdown component for switching between organizational accounts
 *
 * Features:
 * - Shows currently selected organization
 * - Lists all available organizational accounts
 * - Visual indicator (checkmark) for active account
 * - Handles account switching with loading state
 * - Toast notifications for success/error
 *
 * @example
 * ```tsx
 * <AdminAccountSelector
 *   currentAccount={{ id: '123', name: 'Fever', slug: 'fever' }}
 *   accounts={[{ id: '123', name: 'Fever', slug: 'fever' }]}
 *   onSwitch={switchAccountAction}
 * />
 * ```
 */
export function AdminAccountSelector({
  currentAccount,
  accounts,
  onSwitch,
}: AdminAccountSelectorProps) {
  const { t } = useTranslation();
  const router = useRouter();

  const handleSwitch = async (accountId: string) => {
    // Don't switch if already on this account
    if (currentAccount?.id === accountId) {
      return;
    }

    const toastId = toast.loading(t('admin:accountSelector.switching'));

    try {
      const result = await onSwitch(accountId);

      if (result.error) {
        toast.error(t('admin:accountSelector.switchError'), {
          id: toastId,
          description: result.error,
        });
        return;
      }

      toast.success(t('admin:accountSelector.switchSuccess'), { id: toastId });
      router.refresh();
    } catch (error) {
      toast.error(t('admin:accountSelector.switchError'), {
        id: toastId,
        description:
          error instanceof Error
            ? error.message
            : t('admin:accountSelector.unknownError'),
      });
    }
  };

  if (accounts.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          data-test="admin-account-selector-trigger"
        >
          <Building2 className="h-4 w-4" />
          <span className="max-w-[150px] truncate">
            {currentAccount?.name ||
              t('admin:accountSelector.selectPlaceholder')}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[250px]">
        <DropdownMenuLabel>
          <Trans i18nKey="admin:accountSelector.switchLabel" />
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accounts.map((account) => {
          const isActive = currentAccount?.id === account.id;

          return (
            <DropdownMenuItem
              key={account.id}
              onClick={() => handleSwitch(account.id)}
              className="flex cursor-pointer items-center justify-between"
              data-test={`admin-account-selector-item-${account.slug}`}
            >
              <div className="flex flex-col">
                <span className="font-medium">{account.name}</span>
                <span className="text-muted-foreground text-xs">
                  {account.slug}
                </span>
              </div>
              {isActive && (
                <Check
                  className="text-primary h-4 w-4"
                  data-test="active-indicator"
                />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
